import { SidebarProvider } from "@/components/ui";
import { DocumentTitleManager } from "@/components/ui/DocumentTitleManager";
import { type AuthSession } from "@aws-amplify/auth";
import { type QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
import { X } from "lucide-react";
import { lazy, Suspense } from "react";
import toast, { ToastBar, Toaster } from "react-hot-toast";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

const showDevtools =
  import.meta.env.MODE !== "production" &&
  import.meta.env.VITE_SHOW_DEVTOOLS !== "false";

const TanStackRouterDevtools = showDevtools
  ? lazy(() =>
      // Lazy load in development
      import("@tanstack/router-devtools").then((res) => ({
        default: res.TanStackRouterDevtools,
        // For Embedded Mode
        // default: res.TanStackRouterDevtoolsPanel
      })),
    )
  : () => null; // Render nothing when devtools are disabled

interface MyRouterContext {
  // The ReturnType of your useAuth hook or the value of your AuthContext
  session?: AuthSession;
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  component: () => (
    <>
      <SidebarProvider defaultOpen className="bg-shell h-svh overflow-hidden">
        <DocumentTitleManager />
        <Toaster
          position="bottom-right"
          gutter={10}
          containerStyle={{
            bottom: 24,
            right: 24,
          }}
          toastOptions={{
            duration: 4000,
            style: {
              maxWidth: "420px",
              padding: "14px 16px",
              color: "hsl(var(--foreground))",
              background: "hsl(var(--card))",
              border: "1px solid hsl(var(--border))",
              borderRadius: "12px",
              boxShadow: "0 12px 28px rgba(15, 23, 42, 0.14)",
            },
            success: {
              iconTheme: {
                primary: "#16a34a",
                secondary: "#ffffff",
              },
              style: {
                borderColor: "hsl(142 71% 45% / 0.32)",
              },
            },
            error: {
              iconTheme: {
                primary: "hsl(var(--destructive))",
                secondary: "hsl(var(--destructive-foreground))",
              },
              style: {
                borderColor: "hsl(var(--destructive) / 0.4)",
              },
            },
          }}
        >
          {(toastInstance) => (
            <ToastBar toast={toastInstance}>
              {({ icon, message }) => (
                <>
                  {icon}
                  {message}
                  <button
                    type="button"
                    aria-label="Dismiss notification"
                    data-preserve-open-modal="true"
                    className="text-muted-foreground hover:text-foreground -mr-1 ml-1 shrink-0 rounded-sm p-1 transition-colors"
                    onClick={() => {
                      toast.dismiss(toastInstance.id);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </>
              )}
            </ToastBar>
          )}
        </Toaster>

        <Outlet />
      </SidebarProvider>
      {showDevtools && (
        <Suspense>
          <TanStackRouterDevtools
            position="bottom-left"
            toggleButtonProps={{
              style: {
                marginBottom: "8px",
                marginLeft: "8px",
              },
            }}
          />
          <div className="fixed bottom-2 left-30 z-[100000]">
            <ReactQueryDevtools
              initialIsOpen={false}
              buttonPosition="relative"
            />
          </div>
        </Suspense>
      )}
    </>
  ),
});
